import React, { useState } from 'react';
import { QrCodeIcon, DownloadIcon, PrinterIcon, ShareIcon, SettingsIcon, ImageIcon, DollarSignIcon, ClipboardIcon, CheckIcon, InfoIcon, AlertCircleIcon, CopyIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
const MerchantQRGenerator: React.FC = () => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [expiryHours, setExpiryHours] = useState('24');
  const [reference, setReference] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [showQR, setShowQR] = useState(false);
  const [qrURL, setQrURL] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrSize, setQrSize] = useState('medium');
  const [addLogo, setAddLogo] = useState(true);
  const [showColors, setShowColors] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#0066CC');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  // Mock QR code URL
  const mockQRURL = 'https://via.placeholder.com/300x300?text=QR+Code';
  // Templates for QR codes
  const templates = [{
    id: 'default',
    name: 'Default',
    preview: 'https://via.placeholder.com/80x80?text=Default'
  }, {
    id: 'branded',
    name: 'Branded',
    preview: 'https://via.placeholder.com/80x80?text=Branded'
  }, {
    id: 'minimal',
    name: 'Minimal',
    preview: 'https://via.placeholder.com/80x80?text=Minimal'
  }, {
    id: 'colorful',
    name: 'Colorful',
    preview: 'https://via.placeholder.com/80x80?text=Colorful'
  }];
  // Generate a random reference ID
  const generateReference = () => {
    const prefix = 'REF';
    const randomPart = Math.floor(100000 + Math.random() * 900000).toString();
    setReference(`${prefix}-${randomPart}`);
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would call an API to generate the QR code
    // For this mock, we'll just set a placeholder image
    setQrURL(mockQRURL);
    setShowQR(true);
  };
  // Copy QR link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrURL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }, err => {
      console.error('Could not copy text: ', err);
    });
  };
  // Download QR code
  const downloadQR = () => {
    // In a real app, this would download the QR code image
    console.log('Downloading QR code');
  };
  // Print QR code
  const printQR = () => {
    // In a real app, this would open the print dialog
    console.log('Printing QR code');
  };
  // Share QR code
  const shareQR = () => {
    // In a real app, this would open a share dialog
    console.log('Sharing QR code');
  };
  return <div className="space-y-6" data-id="element-3647">
      <div data-id="element-3648">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3649">
          QR Code Generator
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3650">
          Create customized QR codes for payments
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3651">
        {/* QR Generator Form */}
        <div className="lg:col-span-2" data-id="element-3652">
          <Card data-id="element-3653">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-3654">
              Create New QR Code
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-3655">
              <div data-id="element-3656">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3657">
                  Amount (Optional)
                </label>
                <div className="relative rounded-md shadow-sm" data-id="element-3658">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3659">
                    <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-3660" />
                  </div>
                  <input type="number" id="amount" value={amount} onChange={e => setAmount(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" placeholder="Leave empty for customer to enter amount" step="0.01" min="0" data-id="element-3661" />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-3662">
                  If left empty, the customer will be prompted to enter an
                  amount.
                </p>
              </div>
              <div data-id="element-3663">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3664">
                  Description
                </label>
                <input type="text" id="description" value={description} onChange={e => setDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" placeholder="Payment for goods/services" data-id="element-3665" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3666">
                <div data-id="element-3667">
                  <label htmlFor="reference" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3668">
                    Reference ID
                  </label>
                  <div className="flex" data-id="element-3669">
                    <input type="text" id="reference" value={reference} onChange={e => setReference(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md shadow-sm text-sm bg-white dark:bg-gray-800" placeholder="Reference ID" data-id="element-3670" />
                    <button type="button" onClick={generateReference} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-r-md text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600" data-id="element-3671">
                      Generate
                    </button>
                  </div>
                </div>
                <div data-id="element-3672">
                  <label htmlFor="expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3673">
                    Expiry (Hours)
                  </label>
                  <select id="expiry" value={expiryHours} onChange={e => setExpiryHours(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3674">
                    <option value="1" data-id="element-3675">1 hour</option>
                    <option value="2" data-id="element-3676">2 hours</option>
                    <option value="6" data-id="element-3677">6 hours</option>
                    <option value="12" data-id="element-3678">12 hours</option>
                    <option value="24" data-id="element-3679">24 hours</option>
                    <option value="48" data-id="element-3680">48 hours</option>
                    <option value="72" data-id="element-3681">72 hours</option>
                    <option value="168" data-id="element-3682">1 week</option>
                    <option value="720" data-id="element-3683">30 days</option>
                  </select>
                </div>
              </div>
              <div data-id="element-3684">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3685">
                  QR Code Template
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-3686">
                  {templates.map(template => <div key={template.id} className={`cursor-pointer rounded-lg p-2 ${selectedTemplate === template.id ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'}`} onClick={() => setSelectedTemplate(template.id)} data-id="element-3687">
                      <div className="flex flex-col items-center" data-id="element-3688">
                        <img src={template.preview} alt={template.name} className="w-16 h-16 mb-2 rounded" data-id="element-3689" />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-3690">
                          {template.name}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-3691">
                <button type="button" onClick={() => setShowColors(!showColors)} className="flex items-center text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-3" data-id="element-3692">
                  <SettingsIcon className="h-4 w-4 mr-2" data-id="element-3693" />
                  Advanced Options
                  {showColors ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-3694" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-3695" />}
                </button>
                {showColors && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3" data-id="element-3696">
                    <div data-id="element-3697">
                      <label htmlFor="qrSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3698">
                        QR Size
                      </label>
                      <select id="qrSize" value={qrSize} onChange={e => setQrSize(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3699">
                        <option value="small" data-id="element-3700">Small</option>
                        <option value="medium" data-id="element-3701">Medium</option>
                        <option value="large" data-id="element-3702">Large</option>
                      </select>
                    </div>
                    <div data-id="element-3703">
                      <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3704">
                        Primary Color
                      </label>
                      <div className="flex" data-id="element-3705">
                        <input type="color" id="primaryColor" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="h-10 w-10 border border-gray-300 dark:border-gray-600 rounded-l-md" data-id="element-3706" />
                        <input type="text" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3707" />
                      </div>
                    </div>
                    <div data-id="element-3708">
                      <label htmlFor="backgroundColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3709">
                        Background Color
                      </label>
                      <div className="flex" data-id="element-3710">
                        <input type="color" id="backgroundColor" value={backgroundColor} onChange={e => setBackgroundColor(e.target.value)} className="h-10 w-10 border border-gray-300 dark:border-gray-600 rounded-l-md" data-id="element-3711" />
                        <input type="text" value={backgroundColor} onChange={e => setBackgroundColor(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3712" />
                      </div>
                    </div>
                    <div className="md:col-span-3" data-id="element-3713">
                      <div className="flex items-center" data-id="element-3714">
                        <input type="checkbox" id="addLogo" checked={addLogo} onChange={e => setAddLogo(e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-3715" />
                        <label htmlFor="addLogo" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-3716">
                          Add merchant logo to QR code
                        </label>
                      </div>
                    </div>
                  </div>}
              </div>
              <div className="flex justify-end" data-id="element-3717">
                <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-3718">
                  Generate QR Code
                </button>
              </div>
            </form>
          </Card>
        </div>
        {/* QR Code Preview */}
        <div data-id="element-3719">
          <Card data-id="element-3720">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-3721">
              QR Code Preview
            </h3>
            {showQR ? <div className="space-y-4" data-id="element-3722">
                <div className="flex justify-center" data-id="element-3723">
                  <div className="p-4 bg-white rounded-lg shadow-sm" data-id="element-3724">
                    <img src={qrURL} alt="Generated QR Code" className="w-full max-w-[250px] mx-auto" data-id="element-3725" />
                  </div>
                </div>
                <div className="text-center" data-id="element-3726">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3727">
                    {description || 'Payment QR Code'}
                  </p>
                  {amount && <p className="text-lg font-bold text-gray-900 dark:text-white mt-1" data-id="element-3728">
                      PKR {parseFloat(amount).toFixed(2)}
                    </p>}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-3729">
                    Expires in {expiryHours} hours
                  </p>
                  <div className="mt-2 flex items-center justify-center" data-id="element-3730">
                    <div className="relative flex" data-id="element-3731">
                      <input type="text" value={qrURL} readOnly className="pr-10 pl-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 w-full" data-id="element-3732" />
                      <button onClick={copyToClipboard} className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" data-id="element-3733">
                        {copied ? <CheckIcon className="h-4 w-4 text-green-500" data-id="element-3734" /> : <CopyIcon className="h-4 w-4" data-id="element-3735" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center space-x-2 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3736">
                  <button onClick={downloadQR} className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" title="Download" data-id="element-3737">
                    <DownloadIcon className="h-5 w-5" data-id="element-3738" />
                  </button>
                  <button onClick={printQR} className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" title="Print" data-id="element-3739">
                    <PrinterIcon className="h-5 w-5" data-id="element-3740" />
                  </button>
                  <button onClick={shareQR} className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" title="Share" data-id="element-3741">
                    <ShareIcon className="h-5 w-5" data-id="element-3742" />
                  </button>
                </div>
              </div> : <div className="flex flex-col items-center justify-center py-8" data-id="element-3743">
                <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4" data-id="element-3744">
                  <QrCodeIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" data-id="element-3745" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center" data-id="element-3746">
                  Fill out the form and click "Generate QR Code" to create your
                  custom payment QR code.
                </p>
              </div>}
          </Card>
          <div className="mt-4" data-id="element-3747">
            <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-100 dark:border-yellow-800" data-id="element-3748">
              <div className="flex" data-id="element-3749">
                <div className="flex-shrink-0" data-id="element-3750">
                  <InfoIcon className="h-5 w-5 text-yellow-400" data-id="element-3751" />
                </div>
                <div className="ml-3" data-id="element-3752">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300" data-id="element-3753">
                    QR Code Tips
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-200 space-y-1" data-id="element-3754">
                    <p data-id="element-3755">
                      • QR codes with logos may be less scannable in poor
                      lighting
                    </p>
                    <p data-id="element-3756">• Use high contrast colors for better scan rates</p>
                    <p data-id="element-3757">• Print QR codes at least 2x2 cm for physical use</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      {/* QR Code History */}
      <Card data-id="element-3758">
        <div className="flex items-center justify-between mb-4" data-id="element-3759">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-3760">
            Recently Generated QR Codes
          </h3>
          <a href="#" className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300" data-id="element-3761">
            View All
          </a>
        </div>
        <div className="overflow-x-auto" data-id="element-3762">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3763">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-3764">
              <tr data-id="element-3765">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3766">
                  QR Code
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3767">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3768">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3769">
                  Created
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3770">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3771">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3772">
              <tr data-id="element-3773">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3774">
                  <img src="https://via.placeholder.com/40x40?text=QR" alt="QR Code" className="h-10 w-10 rounded" data-id="element-3775" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-3776">
                  Payment for Electronics
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-3777">
                  PKR 1,250.00
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3778">
                  Today, 10:30 AM
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3779">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-3780">
                    Active
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3781">
                  <div className="flex space-x-2" data-id="element-3782">
                    <button className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-3783">
                      <DownloadIcon className="h-4 w-4" data-id="element-3784" />
                    </button>
                    <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-3785">
                      <ShareIcon className="h-4 w-4" data-id="element-3786" />
                    </button>
                  </div>
                </td>
              </tr>
              <tr data-id="element-3787">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3788">
                  <img src="https://via.placeholder.com/40x40?text=QR" alt="QR Code" className="h-10 w-10 rounded" data-id="element-3789" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-3790">
                  Monthly Subscription
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-3791">
                  PKR 750.00
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3792">
                  Yesterday, 2:15 PM
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3793">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-3794">
                    Active
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3795">
                  <div className="flex space-x-2" data-id="element-3796">
                    <button className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-3797">
                      <DownloadIcon className="h-4 w-4" data-id="element-3798" />
                    </button>
                    <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-3799">
                      <ShareIcon className="h-4 w-4" data-id="element-3800" />
                    </button>
                  </div>
                </td>
              </tr>
              <tr data-id="element-3801">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3802">
                  <img src="https://via.placeholder.com/40x40?text=QR" alt="QR Code" className="h-10 w-10 rounded" data-id="element-3803" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-3804">
                  Custom Order #123
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-3805">
                  PKR 2,450.00
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3806">
                  Oct 25, 2023
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3807">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" data-id="element-3808">
                    Expired
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3809">
                  <div className="flex space-x-2" data-id="element-3810">
                    <button className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-3811">
                      <DownloadIcon className="h-4 w-4" data-id="element-3812" />
                    </button>
                    <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100" data-id="element-3813">
                      <ShareIcon className="h-4 w-4" data-id="element-3814" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};
export default MerchantQRGenerator;